<template>
  <div class="ui-input-confirm-component-wrapper">
    <div class="ui-input-confirm-conponent">
      <div class="popup-body">
        <div class="popup-icon" v-if="type" :class="type">
          <i class="fa-solid" :class="typeIcon" />
        </div>
        <h3 class="popup-title" v-text="title" />
        <p class="popup-message" v-text="message" />
        <p :id="confirmKeywordId" class="popup-confirm-keyword" v-text="confirmTextSlug" />
        <input
          type="text"
          v-model="confirmInput"
          class="confirm-input"
          name="input-confirm"
          :aria-labelledby="confirmKeywordId"
        />
      </div>
      <div class="popup-footer">
        <button
          type="button"
          class="confirm-popup-cancel-button"
          @click="sendEvent(false)"
          v-text="cancelButton"
        />
        <button
          type="button"
          :disabled="isDisabled"
          class="confirm-popup-confirm-button"
          @click="sendEvent(true)"
          v-text="confirmButton"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { IInputConfirm } from '@/types/Components/Popup'
  import { Slugger } from '@/utils-ts'
  import { computed, ref, useId } from 'vue'

  const confirmInput = ref<string>('')
  const confirmKeywordId = useId()
  const emit = defineEmits(['response'])
  const {
    type = 'warning',
    title,
    message,
    confirmText,
    confirmKeyword,
    cancelButton,
    confirmButton,
  } = defineProps<IInputConfirm>()

  const confirmTextSlug = computed((): string => {
    return confirmText.replace(confirmKeyword, `"${confirmKeywordSlug.value}"`)
  })

  const confirmKeywordSlug = computed((): string => {
    return Slugger(confirmKeyword)
  })

  const isDisabled = computed((): boolean => {
    return confirmInput.value.toLowerCase().trim() !== confirmKeywordSlug.value
  })

  const typeIcon = computed((): string => {
    switch (type) {
      case 'success':
        return 'fa-circle-check'
      case 'danger':
        return 'fa-circle-xmark'
      case 'warning':
        return 'fa-circle-exclamation'
      case 'info':
        return 'fa-circle-info'
      default:
        return ''
    }
  })

  const sendEvent = (event = false): void => {
    emit('response', event)
  }
</script>

<style lang="scss">
  .ui-input-confirm-component-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    .ui-input-confirm-conponent {
      width: 400px;
      height: auto;
      background-color: #fff;
      border-radius: 5px;
      padding: 1rem;
      opacity: 0;
      animation: fadeIn 0.25s ease-in-out forwards;
      @keyframes fadeIn {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
      @include media(xs, sm, md) {
        width: calc(100% - 1rem);
      }

      .popup-body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        .popup-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 10px;
          font-size: 3rem;
          &.warning {
            color: $warning;
          }
          &.success {
            color: $success;
          }
          &.danger {
            color: $danger;
          }
          &.info {
            color: $info;
          }
        }
        .popup-title {
          font-size: 1.5rem;
          font-weight: 500;
          color: $primary-text-color;
          margin: 1rem 0;
          text-align: center;
        }
        .popup-message {
          font-size: 1rem;
          font-weight: 400;
          text-align: center;
          margin: 0;
        }
        .popup-confirm-keyword {
          font-size: 1rem;
          font-weight: 400;
          text-align: center;
          margin: 0.5rem 0;
        }
        .confirm-input {
          width: 100%;
          height: 2rem;
          border: 1px solid $border-color;
          border-radius: 5px;
          padding: 0 1rem;
          margin-top: 1rem;
          &:focus {
            outline: none;
            border: 1px solid $warning;
          }
        }
      }
      .popup-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 2rem;
        .confirm-popup-cancel-button {
          background-color: $cancel;
          color: $primary-text-color;
          border: none;
          padding: calc(0.5em - 1px) 1em;

          border-radius: 5px;
          margin-right: 10px;
          cursor: pointer;
        }
        .confirm-popup-confirm-button {
          background-color: $success;
          color: $white;
          outline: none;
          border: none;
          padding: calc(0.5em - 1px) 1em;

          border-radius: 5px;
          cursor: pointer;
          transition: all 0.25s ease-in-out;
          &:disabled {
            background-color: $disabled;
            color: $white;
            cursor: not-allowed;
          }
        }
      }
    }
  }
</style>
